﻿export function initPasswordGeneratorPage() {
    const root = document.querySelector(".password-tool-page");

    if (!root) return;

    const page = {
        length: qs("[data-password-length]"),
        minLength: qs("[data-password-min-length]"),
        maxLength: qs("[data-password-max-length]"),
        randomLength: qs("[data-password-random-length]"),

        uppercase: qs("[data-password-uppercase]"),
        lowercase: qs("[data-password-lowercase]"),
        numbers: qs("[data-password-numbers]"),
        symbols: qs("[data-password-symbols]"),
        avoidAmbiguous: qs("[data-password-avoid-ambiguous]"),
        requireEach: qs("[data-password-require-each]"),

        customSymbols: qs("[data-password-custom-symbols]"),
        prefix: qs("[data-password-prefix]"),
        suffix: qs("[data-password-suffix]"),

        batchEnabled: qs("[data-password-batch-enabled]"),
        batchCount: qs("[data-password-batch-count]"),

        generate: qs("[data-password-generate]"),
        output: qs("[data-password-output]"),
        copy: qs("[data-password-copy]"),
        downloadTxt: qs("[data-password-download-txt]"),
        downloadCsv: qs("[data-password-download-csv]"),

        strengthPanel: qs("[data-password-strength-panel]"),
        strengthLabel: qs("[data-password-strength-label]"),
        strengthDesc: qs("[data-password-strength-desc]"),
        strengthScore: qs("[data-password-strength-score]"),
        strengthBar: qs("[data-password-strength-bar]"),

        metaLength: qs("[data-password-meta-length]"),
        metaCount: qs("[data-password-meta-count]"),
        metaEntropy: qs("[data-password-meta-entropy]")
    };

    const liveInputs = [
        page.length,
        page.minLength,
        page.maxLength,
        page.randomLength,
        page.uppercase,
        page.lowercase,
        page.numbers,
        page.symbols,
        page.avoidAmbiguous,
        page.requireEach,
        page.customSymbols,
        page.prefix,
        page.suffix,
        page.batchEnabled,
        page.batchCount
    ];

    liveInputs.filter(Boolean).forEach(input => {
        input.addEventListener("input", () => {
            syncRangeValue(page);
            generatePasswords(page);
        });

        input.addEventListener("change", () => {
            syncLengthRules(page);
            syncRangeValue(page);
            generatePasswords(page);
        });
    });

    page.generate?.addEventListener("click", () => {
        generatePasswords(page, true);
    });

    page.copy?.addEventListener("click", async () => {
        await copyOutput(page);
    });

    page.downloadTxt?.addEventListener("click", () => {
        downloadOutput(page, "txt");
    });

    page.downloadCsv?.addEventListener("click", () => {
        downloadOutput(page, "csv");
    });

    syncLengthRules(page);
    syncRangeValue(page);
    generatePasswords(page);
}

function qs(selector, root = document) {
    return root.querySelector(selector);
}

function syncRangeValue(page) {
    const target = document.querySelector('[data-range-value="data-password-length"]');

    if (target && page.length) {
        target.textContent = page.length.value;
    }
}

function syncLengthRules(page) {
    const min = clamp(toInt(page.minLength?.value, 12), 4, 128);
    const max = clamp(toInt(page.maxLength?.value, 24), 4, 128);

    const safeMin = Math.min(min, max);
    const safeMax = Math.max(min, max);

    if (page.minLength) page.minLength.value = safeMin;
    if (page.maxLength) page.maxLength.value = safeMax;

    const length = clamp(toInt(page.length?.value, 16), 4, 128);

    if (page.length) {
        page.length.value = clamp(length, safeMin, safeMax);
        page.length.min = safeMin;
        page.length.max = safeMax;
    }
}

function generatePasswords(page, animate = false) {
    const options = getOptions(page);
    const count = options.batchEnabled ? options.batchCount : 1;

    const passwords = [];

    for (let i = 0; i < count; i++) {
        passwords.push(generatePassword(options));
    }

    const output = passwords.join("\n");

    if (page.output) {
        page.output.value = output;

        if (animate) {
            page.output.classList.remove("is-generated");
            void page.output.offsetWidth;
            page.output.classList.add("is-generated");
        }
    }

    updateStrength(page, passwords, options);
    updateMeta(page, passwords, options);
}

function getOptions(page) {
    const randomLength = Boolean(page.randomLength?.checked);

    const minLength = clamp(toInt(page.minLength?.value, 12), 4, 128);
    const maxLength = clamp(toInt(page.maxLength?.value, 24), minLength, 128);

    const fixedLength = clamp(toInt(page.length?.value, 16), minLength, maxLength);

    const batchEnabled = Boolean(page.batchEnabled?.checked);
    const batchCount = clamp(toInt(page.batchCount?.value, 10), 1, 1000);

    return {
        length: fixedLength,
        minLength,
        maxLength,
        randomLength,
        uppercase: Boolean(page.uppercase?.checked),
        lowercase: Boolean(page.lowercase?.checked),
        numbers: Boolean(page.numbers?.checked),
        symbols: Boolean(page.symbols?.checked),
        avoidAmbiguous: Boolean(page.avoidAmbiguous?.checked),
        requireEach: Boolean(page.requireEach?.checked),
        customSymbols: page.customSymbols?.value || "!@#$%^&*()-_=+[]{};:,.?/|~",
        prefix: page.prefix?.value || "",
        suffix: page.suffix?.value || "",
        batchEnabled,
        batchCount
    };
}

function generatePassword(options) {
    const sets = buildCharacterSets(options);

    if (!sets.length) {
        return "";
    }

    const length = options.randomLength
        ? secureRandomInt(options.minLength, options.maxLength)
        : options.length;

    const prefix = options.prefix || "";
    const suffix = options.suffix || "";

    const coreLength = Math.max(0, length - prefix.length - suffix.length);

    if (coreLength <= 0) {
        return `${prefix}${suffix}`.slice(0, length);
    }

    const chars = sets.join("");
    const required = [];

    if (options.requireEach) {
        sets.forEach(set => {
            required.push(randomChar(set));
        });
    }

    const passwordChars = [...required];

    while (passwordChars.length < coreLength) {
        passwordChars.push(randomChar(chars));
    }

    shuffle(passwordChars);

    return `${prefix}${passwordChars.slice(0, coreLength).join("")}${suffix}`;
}

function buildCharacterSets(options) {
    let upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let lower = "abcdefghijklmnopqrstuvwxyz";
    let number = "0123456789";
    let symbol = options.customSymbols || "!@#$%^&*()-_=+[]{};:,.?/|~";

    if (options.avoidAmbiguous) {
        const ambiguous = new Set(["O", "0", "I", "l", "1", "|", "`", "'", "\""]);
        upper = removeAmbiguous(upper, ambiguous);
        lower = removeAmbiguous(lower, ambiguous);
        number = removeAmbiguous(number, ambiguous);
        symbol = removeAmbiguous(symbol, ambiguous);
    }

    const sets = [];

    if (options.uppercase && upper) sets.push(upper);
    if (options.lowercase && lower) sets.push(lower);
    if (options.numbers && number) sets.push(number);
    if (options.symbols && symbol) sets.push(uniqueChars(symbol));

    return sets;
}

function removeAmbiguous(value, ambiguous) {
    return [...value].filter(ch => !ambiguous.has(ch)).join("");
}

function uniqueChars(value) {
    return [...new Set([...String(value)])].join("");
}

function randomChar(chars) {
    if (!chars.length) return "";

    return chars[secureRandomInt(0, chars.length - 1)];
}

function secureRandomInt(min, max) {
    const lower = Math.ceil(min);
    const upper = Math.floor(max);

    if (upper <= lower) return lower;

    const range = upper - lower + 1;
    const maxRange = 256;
    const limit = Math.floor(maxRange / range) * range;

    const buffer = new Uint8Array(1);

    let value;

    do {
        crypto.getRandomValues(buffer);
        value = buffer[0];
    } while (value >= limit);

    return lower + (value % range);
}

function shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = secureRandomInt(0, i);
        [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
}

function updateStrength(page, passwords, options) {
    const password = passwords[0] || "";
    const entropy = calculateEntropyBits(password, options);
    const score = Math.min(100, Math.round((entropy / 120) * 100));

    let label = "Weak";
    let desc = "Increase length or enable more character types.";
    let state = "weak";

    if (score >= 85) {
        label = "Excellent";
        desc = "Very strong password settings.";
        state = "excellent";
    } else if (score >= 65) {
        label = "Strong";
        desc = "Good length and mixed character types.";
        state = "strong";
    } else if (score >= 42) {
        label = "Fair";
        desc = "Usable, but longer passwords are recommended.";
        state = "fair";
    }

    if (page.strengthPanel) {
        page.strengthPanel.dataset.strength = state;
    }

    if (page.strengthLabel) page.strengthLabel.textContent = label;
    if (page.strengthDesc) page.strengthDesc.textContent = desc;
    if (page.strengthScore) page.strengthScore.textContent = `${score}%`;
    if (page.strengthBar) page.strengthBar.style.width = `${score}%`;
}

function updateMeta(page, passwords, options) {
    const password = passwords[0] || "";
    const entropy = calculateEntropyBits(password, options);

    if (page.metaLength) {
        page.metaLength.textContent = String(password.length);
    }

    if (page.metaCount) {
        page.metaCount.textContent = String(passwords.length);
    }

    if (page.metaEntropy) {
        page.metaEntropy.textContent = `${Math.round(entropy)} bit`;
    }
}

function calculateEntropyBits(password, options) {
    const sets = buildCharacterSets(options);
    const poolSize = sets.join("").length;

    if (!password || poolSize <= 1) return 0;

    return password.length * Math.log2(poolSize);
}

async function copyOutput(page) {
    const value = page.output?.value || "";

    if (!value) return;

    try {
        await navigator.clipboard.writeText(value);
        showButtonDone(page.copy, "Copied");
    } catch {
        fallbackCopy(value);
        showButtonDone(page.copy, "Copied");
    }
}

function downloadOutput(page, format) {
    const value = page.output?.value || "";

    if (!value) return;

    const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-");

    if (format === "csv") {
        const rows = value
            .split("\n")
            .filter(Boolean)
            .map((password, index) => `${index + 1},"${password.replaceAll("\"", "\"\"")}"`);

        const csv = ["Index,Password", ...rows].join("\n");

        downloadFile(csv, `passwords-${timestamp}.csv`, "text/csv;charset=utf-8");
        return;
    }

    downloadFile(value, `passwords-${timestamp}.txt`, "text/plain;charset=utf-8");
}

function downloadFile(content, fileName, type) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
}

function fallbackCopy(value) {
    const input = document.createElement("textarea");
    input.value = value;
    input.style.position = "fixed";
    input.style.opacity = "0";
    input.style.pointerEvents = "none";

    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    input.remove();
}

function showButtonDone(button, text) {
    if (!button) return;

    const oldHtml = button.innerHTML;

    button.classList.add("is-done");
    button.innerHTML = `<i class="fa-regular fa-check"></i><span>${text}</span>`;

    window.setTimeout(() => {
        button.innerHTML = oldHtml;
        button.classList.remove("is-done");
    }, 1100);
}

function toInt(value, fallback) {
    const parsed = Number.parseInt(value, 10);

    return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}